import { Product, Product_TallyDimension } from "gen/product/models_pb"
import { Decimal as PBDecimal } from "gen/proto/decimal/models_pb"
import { SaleProduct, SaleProduct_TallyRow } from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"

import { FormErrors } from "hooks/useForm"

import { isReturn } from "./saleProduct"
import { TallyFormData } from "../NotesModal/modalNotes.types"

export function isTallyProduct(product?: Product): boolean {
  return (
    !!product && product.tallyDimension !== Product_TallyDimension.UNDEFINED
  )
}

export function tallyHasWidth(dimension: Product_TallyDimension): boolean {
  return (
    dimension === Product_TallyDimension.UNIFIED_LENGTH ||
    dimension === Product_TallyDimension.AREA
  )
}

export function emptyTallyRow(
  dimension: Product_TallyDimension
): SaleProduct_TallyRow {
  return SaleProduct_TallyRow.create({
    quantity: Decimal.fromBigSource(1),
    length: Decimal.zero(),
    ...(tallyHasWidth(dimension) ? { width: Decimal.zero() } : {}),
  })
}

export function tallyRowTotal(
  row: SaleProduct_TallyRow,
  dimension: Product_TallyDimension
): Decimal {
  const quantity = Decimal.fromPBOrZero(row.quantity)
  const length = Decimal.fromPBOrZero(row.length)
  const width = Decimal.fromPBOrZero(row.width)
  switch (dimension) {
    case Product_TallyDimension.UNIFIED_LENGTH:
      return quantity.mult(length.add(width))
    case Product_TallyDimension.AREA:
      return quantity.mult(length).mult(width)
    default:
      return quantity.mult(length)
  }
}

export function tallyTotal(
  rows: SaleProduct_TallyRow[],
  dimension: Product_TallyDimension
): Decimal {
  return rows.reduce(
    (sum, row) => sum.add(tallyRowTotal(row, dimension)),
    Decimal.zero()
  )
}

export function tallyUomCode(sp: SaleProduct): string | undefined {
  const snapshot = isReturn(sp) ? sp.uomSnapshotReturned : sp.uomSnapshot
  return snapshot?.uomCode
}

export function isTallyBlank(rows: SaleProduct_TallyRow[]): boolean {
  return rows.every(
    (row) =>
      Decimal.fromPBOrZero(row.quantity).isZero() &&
      Decimal.fromPBOrZero(row.length).isZero() &&
      Decimal.fromPBOrZero(row.width).isZero()
  )
}

export function validateTallyRows(
  rows: SaleProduct_TallyRow[],
  dimension: Product_TallyDimension,
  allowDecimalQuantities: boolean
): FormErrors<TallyFormData> {
  const errors: FormErrors<TallyFormData> = {}
  const measurementError = (label: string, value: Decimal) => {
    if (!value.isPositive()) {
      return `${label} must be greater than 0`
    }
    if (!allowDecimalQuantities && value.places() > 0) {
      return `${label} must be a whole number for this product`
    }
    return undefined
  }
  rows.forEach((row, index) => {
    const quantity = Decimal.fromPBOrZero(row.quantity)
    if (!quantity.isPositive() || quantity.places() > 0) {
      errors[`tallyRows.${index}.quantity`] =
        "Quantity must be a whole number greater than 0"
    }
    const lengthError = measurementError(
      "Length",
      Decimal.fromPBOrZero(row.length)
    )
    if (lengthError) {
      errors[`tallyRows.${index}.length`] = lengthError
    }
    const widthError = tallyHasWidth(dimension)
      ? measurementError("Width", Decimal.fromPBOrZero(row.width))
      : undefined
    if (widthError) {
      errors[`tallyRows.${index}.width`] = widthError
    }
  })
  return errors
}

export function areTallyRowsEqual(
  first: SaleProduct_TallyRow[],
  second: SaleProduct_TallyRow[]
): boolean {
  if (first.length !== second.length) {
    return false
  }
  const eq = (a?: PBDecimal, b?: PBDecimal) =>
    Decimal.fromPBOrZero(a).eq(Decimal.fromPBOrZero(b))
  return first.every(
    (row, index) =>
      eq(row.quantity, second[index].quantity) &&
      eq(row.length, second[index].length) &&
      eq(row.width, second[index].width)
  )
}

export function formatTallyRows(
  rows: SaleProduct_TallyRow[],
  dimension: Product_TallyDimension,
  uomCode: string
): string {
  return rows
    .map((row) => {
      const quantity = Decimal.fromPBOrZero(row.quantity).toString()
      const length = Decimal.fromPBOrZero(row.length).toString()
      const width = Decimal.fromPBOrZero(row.width).toString()
      const part =
        dimension === Product_TallyDimension.UNIFIED_LENGTH
          ? `${quantity} × (${length} + ${width})`
          : dimension === Product_TallyDimension.AREA
            ? `${quantity} × ${length} × ${width}`
            : `${quantity} × ${length}`
      return uomCode ? `${part} ${uomCode}` : part
    })
    .join(", ")
}
